import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';

interface UserProfile {
  id: string;
  username: string;
  avatarUrl: string;
  interests: string[];
}

const WelcomeToTheTribeCarousel: React.FC = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    loadUsers();
  }, [currentUser?.id]);

  const loadUsers = async () => {
    try {
      setIsLoading(true);

      // Fetch users excluding current user, admins, and test accounts
      // Get profiles with join to auth.users for email filtering
      const { data, error } = await supabase
        .rpc('get_tribe_members_for_carousel', {
          requesting_user_id: currentUser?.id || null
        });

      if (error) {
        console.error('Error fetching users via RPC:', error);
        // Fallback to direct query if RPC doesn't exist
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('profiles')
          .select('id, username, avatar_url, interests')
          .neq('id', currentUser?.id || '')
          .eq('is_admin', false)
          .eq('profile_complete', true)
          .not('username', 'ilike', '%test%')
          .order('created_at', { ascending: false })
          .limit(20);

        if (fallbackError) throw fallbackError;

        const profiles: UserProfile[] = (fallbackData || []).map(profile => ({
          id: profile.id,
          username: profile.username || 'User',
          avatarUrl: profile.avatar_url || '',
          interests: profile.interests || []
        }));

        setUsers(profiles);
        return;
      }

      const profiles: UserProfile[] = (data || []).map((profile: any) => ({
        id: profile.id,
        username: profile.username || 'User',
        avatarUrl: profile.avatar_url || '',
        interests: profile.interests || []
      }));

      setUsers(profiles);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [users]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setTimeout(checkScroll, 100);
    }
  };

  const getInitials = (username: string) => {
    return username.slice(0, 2).toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="relative">
        <div className="flex gap-3 overflow-hidden pb-2">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="flex-shrink-0 w-[180px] bg-theme-cards rounded-xl p-3 animate-pulse">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 bg-theme-bg-secondary rounded-full" />
                <div className="h-3 bg-theme-bg-secondary rounded w-20 flex-1" />
              </div>
              <div className="flex flex-wrap gap-1">
                <div className="h-5 bg-theme-bg-secondary rounded-full w-16" />
                <div className="h-5 bg-theme-bg-secondary rounded-full w-20" />
                <div className="h-5 bg-theme-bg-secondary rounded-full w-14" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (users.length === 0) return null;

  return (
    <div className="relative">
      <div
        ref={scrollContainerRef}
        onScroll={checkScroll}
        className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {users.map((user) => (
          <div
            key={user.id}
            className="flex-shrink-0 w-[180px] bg-theme-cards rounded-xl p-3 shadow-sm"
          >
            {/* Avatar and username - horizontal layout */}
            <div className="flex items-center gap-2 mb-2">
              <div className="relative flex-shrink-0">
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.username}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-cyan-500 flex items-center justify-center text-white font-bold text-xs">
                    {getInitials(user.username)}
                  </div>
                )}
              </div>
              <h3 className="font-semibold text-sm text-theme-text-main truncate flex-1">{user.username}</h3>
            </div>

            {/* Interests - compact inline format */}
            {user.interests && user.interests.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {user.interests.slice(0, 3).map((interest, idx) => (
                  <span
                    key={idx}
                    className="text-[10px] px-1.5 py-0.5 bg-theme-bg-secondary rounded text-theme-text-secondary leading-tight"
                  >
                    {interest}
                  </span>
                ))}
                {user.interests.length > 3 && (
                  <span className="text-[10px] px-1.5 py-0.5 text-theme-text-tertiary leading-tight">
                    +{user.interests.length - 3}
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation buttons */}
      {users.length > 5 && (
        <>
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-theme-background/90 backdrop-blur-sm text-theme-text-main p-2 rounded-full hover:bg-theme-cards transition-colors z-10 shadow-lg border border-theme-cards"
              aria-label="Scroll left"
            >
              <ChevronLeft size={18} />
            </button>
          )}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-theme-background/90 backdrop-blur-sm text-theme-text-main p-2 rounded-full hover:bg-theme-cards transition-colors z-10 shadow-lg border border-theme-cards"
              aria-label="Scroll right"
            >
              <ChevronRight size={18} />
            </button>
          )}
        </>
      )}

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default WelcomeToTheTribeCarousel;
